import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckSquare, Zap, Trash2, Pencil } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  getQuickTasks, createQuickTask, updateQuickTask, updateQuickTaskStatus, deleteQuickTask, getStaff,
} from "../lib/api";
import { useAuth } from "../App";

function daysFromToday(yyyyMmDd) {
  if (!yyyyMmDd) return null;
  const d = new Date(yyyyMmDd + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

function DueBadge({ date, done }) {
  if (done) return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Done</Badge>;
  if (!date) return <span className="text-muted-foreground text-sm">—</span>;
  const diff = daysFromToday(date);
  if (diff < 0) return <Badge className="bg-red-100 text-red-700 border-red-200">Overdue {Math.abs(diff)}d</Badge>;
  if (diff === 0) return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Due today</Badge>;
  if (diff <= 3) return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Due in {diff}d</Badge>;
  return <Badge variant="outline">Due {date}</Badge>;
}

function TaskDialog({ open, onOpenChange, editing, staff, currentUserId, onSaved }) {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assigneeId, setAssigneeId] = useState("me");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTitle(editing.title || "");
      setDueDate(editing.due_date || "");
      setAssigneeId(editing.assignee_id === currentUserId || !editing.assignee_id ? "me" : editing.assignee_id);
    } else {
      setTitle("");
      setDueDate("");
      setAssigneeId("me");
    }
  }, [open, editing, currentUserId]);

  const handleSave = async () => {
    if (!title.trim()) { toast.error("What's the task?"); return; }
    setSaving(true);
    const realAssignee = assigneeId === "me" ? currentUserId : assigneeId;
    try {
      const payload = { title: title.trim(), assignee_id: realAssignee, due_date: dueDate || null };
      if (editing) await updateQuickTask(editing.id, payload);
      else await createQuickTask(payload);
      toast.success("Saved");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Quick Task" : "Quick Task"}</DialogTitle>
          <DialogDescription>A simple to-do — assign it, set a due date, done.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Task *</Label>
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              placeholder="e.g., Call Mom Smith re: pickup"
              data-testid="quick-task-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="quick-task-due" />
            </div>
            <div className="space-y-1">
              <Label>Assign to</Label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger data-testid="quick-task-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="me">Me</SelectItem>
                  {staff.filter((s) => s.id !== currentUserId).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="quick-task-save">
            {saving ? "..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function QuickTasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("open"); // open | done | all

  const load = async () => {
    try {
      const res = await getQuickTasks();
      setTasks(res.data);
    } catch {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStaff().then((r) => setStaff([...(r.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
    load();
  }, []);

  const shown = useMemo(() => {
    if (filter === "all") return tasks;
    return tasks.filter((t) => (filter === "done" ? t.status === "done" : t.status !== "done"));
  }, [tasks, filter]);

  const openCounts = useMemo(() => ({
    open: tasks.filter((t) => t.status !== "done").length,
    done: tasks.filter((t) => t.status === "done").length,
  }), [tasks]);

  const toggle = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    setTasks((list) => list.map((x) => (x.id === t.id ? { ...x, status: next } : x)));
    try {
      await updateQuickTaskStatus(t.id, next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
      load();
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await deleteQuickTask(t.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setDialogOpen(true); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00305E]" /></div>;

  return (
    <div className="space-y-6" data-testid="quick-tasks-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#00305E]">Quick Tasks</h1>
          <p className="text-muted-foreground">{openCounts.open} open · {openCounts.done} done</p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="quick-task-btn">
          <Zap className="w-4 h-4 mr-1" /> Quick Task
        </Button>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white border">
          <TabsTrigger value="open" data-testid="qt-tab-open">Open <Badge className="ml-2 bg-amber-100 text-amber-800">{openCounts.open}</Badge></TabsTrigger>
          <TabsTrigger value="done" data-testid="qt-tab-done">Completed <Badge className="ml-2 bg-green-100 text-green-800">{openCounts.done}</Badge></TabsTrigger>
          <TabsTrigger value="all" data-testid="qt-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {shown.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">
          <CheckSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
          {filter === "done" ? "No completed tasks yet." : "No tasks yet. Tap “Quick Task” to add one."}
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="quick-tasks-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="p-3 w-10"></th>
                  <th className="p-3">Task</th>
                  <th className="p-3 w-40">Assigned to</th>
                  <th className="p-3 w-36">Due date</th>
                  <th className="p-3 w-28">Status</th>
                  <th className="p-3 w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => {
                  const done = t.status === "done";
                  return (
                    <tr key={t.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`quick-task-row-${t.id}`}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={done}
                          onChange={() => toggle(t)}
                          className="w-4 h-4 cursor-pointer"
                          data-testid={`quick-task-toggle-${t.id}`}
                        />
                      </td>
                      <td className={`p-3 font-medium ${done ? "line-through text-muted-foreground" : ""}`} data-testid={`quick-task-title-${t.id}`}>
                        {t.title}
                      </td>
                      <td className="p-3">{t.assignee_id === user?.id ? "Me" : (t.assignee_name || "Unassigned")}</td>
                      <td className="p-3"><DueBadge date={t.due_date} done={done} /></td>
                      <td className="p-3">
                        {done
                          ? <Badge className="bg-green-100 text-green-800">Complete</Badge>
                          : <Badge className="bg-amber-100 text-amber-800">Open</Badge>}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`quick-task-edit-${t.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(t)} data-testid={`quick-task-delete-${t.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <TaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        staff={staff}
        currentUserId={user?.id}
        onSaved={load}
      />
    </div>
  );
}
